// Minimal JSON API client for the CRM web shell.
//
// - Always prefixes `/api/v1` so callers write domain paths (e.g. `/auth/me`).
// - Sends `credentials: 'include'` so the session cookie flows on same-origin
//   (Vite proxy) and cross-origin (prod) alike.
// - Sends `X-Workspace-Id`, когда пользователь выбрал workspace в шапке. Это
//   выбор, а не доказательство доступа: членство проверяет `withScope`.
// - Rejects with a structured `ApiError` for non-2xx responses and surfaces
//   the backend's `{error, code?, details?}` envelope when present.
//
// - GET requests retry up to 3× with exponential backoff + jitter on network
//   errors, HTTP 429 (Rate-Limit), and 5xx (Server Error).  Retry-After
//   header is honoured for 429 responses (capped at 30 s).  Non-GET requests
//   never retry.
// - Every verb accepts an optional AbortSignal for caller‑side cancellation.
//   AbortError aborts immediately without retry.

import { readActiveWorkspaceId } from '../scope/activeWorkspace.js';

const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: unknown;

  constructor(status: number, message: unknown, code?: string, details?: unknown) {
    super(normalizeErrorMessage(message, `HTTP ${status}`));
    this.name = 'ApiError';
    this.status = status;
    if (code !== undefined) {
      this.code = code;
    }
    if (details !== undefined) {
      this.details = details;
    }
  }
}

function normalizeErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    for (const key of ['message', 'error', 'detail']) {
      const nested = record[key];
      if (typeof nested === 'string' && nested.trim() !== '') return nested;
    }
  }
  return fallback;
}

interface ErrorBody {
  error?: unknown;
  message?: unknown;
  code?: string;
  details?: unknown;
}

async function parseErrorBody(res: Response): Promise<ErrorBody> {
  try {
    const data = (await res.json()) as ErrorBody;
    return data;
  } catch {
    return {};
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await parseErrorBody(res);
    const message = body.message ?? body.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body.code, body.details);
  }
  // 204 No Content or empty body → return undefined-ish cast to T.
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const normalised = path.startsWith('/') ? path : `/${path}`;
  return `${API_PREFIX}${normalised}`;
}

/* ── Retry helpers ─────────────────────────────────────────────────────── */

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Exponential backoff with jitter, matching the contract:
 *   delay = min(base * 2^attempt + random(0, base * 2^attempt * 0.5), 5000)
 * where `base` is 300 ms.
 */
function backoffDelay(attempt: number): number {
  const exponential = 300 * 2 ** attempt;
  const jitter = Math.random() * exponential * 0.5;
  return Math.min(exponential + jitter, 5000);
}

/**
 * Parse `Retry-After` header value.  The spec allows an HTTP-date or a
 * decimal seconds integer.  We try integer first, then fall back to the
 * date-or-nothing path.
 *
 * Returns `undefined` when the header is absent or unparseable.
 */
function parseRetryAfter(header: string | null): number | undefined {
  if (header === null) return undefined;

  // Try integer seconds
  const seconds = Number.parseInt(header, 10);
  if (!Number.isNaN(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, 30_000); // cap at 30 s
  }

  // Try HTTP-date
  const ts = Date.parse(header);
  if (!Number.isNaN(ts)) {
    const delay = ts - Date.now();
    return delay > 0 ? Math.min(delay, 30_000) : 0;
  }

  return undefined;
}

const MAX_RETRIES = 2; // 2 retries = 3 total attempts

/**
 * Wraps `fetch` with bounded retry for GET requests and bare forwarding for
 * all other methods.
 *
 * **GET** — retries on:
 *   - Network errors (`TypeError`)
 *   - HTTP 429 (Rate-Limit) — respects `Retry-After` header
 *   - HTTP 5xx (Server Error)
 *
 * **Never retries** on:
 *   - `AbortError` (thrown immediately)
 *   - HTTP 4xx except 429
 *   - HTTP 501 (Not Implemented) — a permanent condition, so a retry can only
 *     delay the error and multiply the request count
 *   - Non‑GET methods (single attempt only)
 */
async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();

  // Only GET requests are idempotent — no retry for other methods
  if (method !== 'GET') {
    return fetch(url, init);
  }

  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(url, init);

      // Successful response — done
      if (res.ok) return res;

      // 4xx (except 429) → not retryable, let caller handle
      if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        return res;
      }

      // 501 Not Implemented is permanent — the server will not start supporting
      // the feature between two attempts. Retrying only delays the error and
      // triples the request count (the L3 memory search, whose embeddings
      // provider is not wired, went out three times per attempt).
      if (res.status === 501) return res;

      // 429 or 5xx — may retry
      if (attempt >= MAX_RETRIES) return res;

      // Pick delay: Retry-After for 429, exponential backoff otherwise
      const delayMs =
        res.status === 429
          ? (parseRetryAfter(res.headers.get('Retry-After')) ?? backoffDelay(attempt))
          : backoffDelay(attempt);

      await sleep(delayMs);
    } catch (err) {
      // AbortError propagates immediately — no retry
      if (err instanceof DOMException && err.name === 'AbortError') throw err;

      // Network error (TypeError) or anything else — retry if budget remains
      if (attempt >= MAX_RETRIES) throw err;

      await sleep(backoffDelay(attempt));
    }
  }
}

/**
 * Общие заголовки запроса + выбранный workspace, если он есть.
 * Экспортируется для тех немногих мест, где нужен «сырой» fetch (SSE-стрим чата):
 * иначе такой запрос уйдёт в дефолтный workspace, а не в выбранный.
 */
export function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const workspaceId = readActiveWorkspaceId();
  if (workspaceId !== null) {
    headers['X-Workspace-Id'] = workspaceId;
  }
  return headers;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetchWithRetry(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
    headers: buildHeaders(),
    ...(signal === undefined ? {} : { signal }),
  });
  return handleResponse<T>(res);
}

export async function apiPost<T, B = unknown>(
  path: string,
  body: B,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetchWithRetry(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: buildHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    ...(signal === undefined ? {} : { signal }),
  });
  return handleResponse<T>(res);
}

/** PUT — для ресурсов с семантикой «создать или заменить целиком» (upsert). */
export async function apiPut<T, B = unknown>(
  path: string,
  body: B,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetchWithRetry(buildUrl(path), {
    method: 'PUT',
    credentials: 'include',
    headers: buildHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    ...(signal === undefined ? {} : { signal }),
  });
  return handleResponse<T>(res);
}

export async function apiPatch<T, B = unknown>(
  path: string,
  body: B,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetchWithRetry(buildUrl(path), {
    method: 'PATCH',
    credentials: 'include',
    headers: buildHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    ...(signal === undefined ? {} : { signal }),
  });
  return handleResponse<T>(res);
}

/** Тело у DELETE — для пакетных операций («удали вот эти id»). */
export async function apiDelete<T = void, B = unknown>(
  path: string,
  body?: B,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetchWithRetry(buildUrl(path), {
    method: 'DELETE',
    credentials: 'include',
    headers: buildHeaders(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  });
  return handleResponse<T>(res);
}
